import { useCallback, useEffect, useState } from 'react';
import {
    ANALYTICS_AVAILABLE,
    needsDecision,
    setConsent,
    subscribeToConsent
} from './consent';
import { LEGAL_PATHS } from '../Legal';
import './CookieConsent.css';

export {
    ANALYTICS_AVAILABLE,
    CONSENT_CHANGED_EVENT,
    getConsent,
    hasConsent,
    needsDecision,
    resetConsent,
    setConsent,
    subscribeToConsent
} from './consent';

/**
 * Cookie banner.
 *
 * Renders one of two forms depending on whether optional tracking exists:
 *
 *  - ANALYTICS_AVAILABLE false (today): a notice. Every cookie the app sets is
 *    strictly necessary for sign-in, which does not require consent, so
 *    presenting an accept/reject choice that gates nothing would misrepresent
 *    what the button does.
 *  - ANALYTICS_AVAILABLE true: a real choice, with accept and reject given
 *    equal prominence and rejection wired to hasConsent('analytics').
 *
 * Mounted in index.js rather than App.js so it appears on every screen,
 * including signed-out and onboarding, without touching App's branches.
 */
export function CookieConsent() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Read in an effect, not during render: localStorage access can throw
        // in private-browsing modes, and this keeps the first paint clean.
        setVisible(needsDecision());

        // Another tab, or the footer's "Cookie preferences" link, can clear the
        // stored decision; re-show the banner when that happens.
        return subscribeToConsent(() => setVisible(needsDecision()));
    }, []);

    const decide = useCallback((analytics) => {
        setConsent({ analytics });
        setVisible(false);
    }, []);

    if (!visible) return null;

    return (
        <div
            className="cookie-banner"
            role="region"
            aria-label="Cookie notice"
        >
            <div className="cookie-banner-inner">
                {ANALYTICS_AVAILABLE ? (
                    <p className="cookie-banner-copy">
                        We use cookies that are necessary to sign you in and keep you signed in, and
                        optional cookies that help us understand how Vixo is used. You can accept or
                        reject the optional ones. See our{' '}
                        <a href={LEGAL_PATHS.privacy}>Privacy Policy</a>.
                    </p>
                ) : (
                    <p className="cookie-banner-copy">
                        Vixo uses only the cookies and browser storage needed to sign you in and keep
                        you signed in. We don&apos;t use advertising or cross-site tracking cookies.
                        Read more in our <a href={LEGAL_PATHS.privacy}>Privacy Policy</a>.
                    </p>
                )}

                <div className="cookie-banner-actions">
                    {ANALYTICS_AVAILABLE && (
                        <button
                            type="button"
                            className="cookie-btn cookie-btn-secondary"
                            onClick={() => decide(false)}
                        >
                            Reject optional
                        </button>
                    )}
                    <button
                        type="button"
                        className="cookie-btn cookie-btn-primary"
                        onClick={() => decide(ANALYTICS_AVAILABLE)}
                    >
                        {ANALYTICS_AVAILABLE ? 'Accept all' : 'Got it'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CookieConsent;
